/**
 * Login page - Clean, minimal, modern design
 * IMPORTANT: This page is always rendered in light mode for consistency
 */
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types/user';
import PageTransition from '../components/common/PageTransition';
import {
  Container,
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  ThemeProvider,
} from '@mui/material';
import { createAppTheme } from '../theme/theme';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ssoLogin } = useAuth();
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const { instance } = useMsal();

  const handleMicrosoftLogin = () => {
    instance.loginRedirect(loginRequest);
  };

  const { accounts } = useMsal();
  const [ssoProcessing, setSsoProcessing] = useState(false);

  useEffect(() => {
    const completeSsoLogin = async () => {
      if (accounts.length === 0 || ssoProcessing) return;

      setSsoProcessing(true);
      setError('');

      try {
        const idToken = accounts[0].idToken;
        if (!idToken) return;

        await ssoLogin(idToken);

        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : null;
        const userRole = user?.role;
        const mustChangePassword = user?.must_change_password;

        if (mustChangePassword === true) {
          if (userRole === UserRole.EMPLOYEE) {
            navigate('/employee/profile?tab=password&required=true');
          } else {
            navigate('/profile?tab=password&required=true');
          }
        } else {
          if (userRole === UserRole.EMPLOYEE) {
            navigate('/employee/dashboard');
          } else {
            navigate('/dashboard');
          }
        }
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Microsoft sign-in failed. Please try again.');
      } finally {
        setSsoProcessing(false);
      }
    };

    completeSsoLogin();
  }, [accounts]);

  // Force light mode for login page to ensure consistent appearance
  const lightTheme = createAppTheme('light');

  useEffect(() => {
    const state = location.state as { message?: string } | null;
    if (state?.message) {
      setInfoMessage(state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  return (
    <ThemeProvider theme={lightTheme}>
      <PageTransition>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            // Clean white background with subtle warmth
            background: `
              linear-gradient(135deg, #FFFFFF 0%, #FAFAFA 25%, #F5F5F5 50%, #FAFAFA 75%, #FFFFFF 100%)
            `,
            overflow: 'hidden',
          }}
        >
          {/* Large Grid Background */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
              linear-gradient(rgba(249, 115, 22, 0.06) 1px, transparent 1px),
              linear-gradient(90deg, rgba(249, 115, 22, 0.06) 1px, transparent 1px)
            `,
              backgroundSize: '50px 50px',
              zIndex: 0,
            }}
          />

          {/* Gradient Orb - Top Right */}
          <Box
            sx={{
              position: 'absolute',
              top: '-25%',
              right: '-15%',
              width: '700px',
              height: '700px',
              background: 'radial-gradient(circle, rgba(249, 115, 22, 0.12) 0%, rgba(251, 146, 60, 0.04) 35%, transparent 65%)',
              borderRadius: '50%',
              filter: 'blur(60px)',
              zIndex: 0,
            }}
          />

          {/* Gradient Orb - Bottom Left */}
          <Box
            sx={{
              position: 'absolute',
              bottom: '-20%',
              left: '-10%',
              width: '600px',
              height: '600px',
              background: 'radial-gradient(circle, rgba(234, 88, 12, 0.08) 0%, rgba(249, 115, 22, 0.03) 40%, transparent 65%)',
              borderRadius: '50%',
              filter: 'blur(70px)',
              zIndex: 0,
            }}
          />

          {/* Gradient Orb - Center (behind card) */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '500px',
              height: '500px',
              background: 'radial-gradient(circle, rgba(249, 115, 22, 0.1) 0%, rgba(251, 146, 60, 0.04) 40%, transparent 70%)',
              borderRadius: '50%',
              filter: 'blur(50px)',
              zIndex: 0,
            }}
          />

          {/* Gradient Orb - Top Left (subtle) */}
          <Box
            sx={{
              position: 'absolute',
              top: '10%',
              left: '-5%',
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle, rgba(251, 146, 60, 0.06) 0%, transparent 60%)',
              borderRadius: '50%',
              filter: 'blur(50px)',
              zIndex: 0,
            }}
          />

          {/* Accent Line - Top */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, transparent 0%, #F97316 50%, transparent 100%)',
              zIndex: 1,
            }}
          />

          <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {/* Logo */}
              <Box
                component="img"
                src="/Datafact Z New Logo Dark.png"
                alt="DataFactz"
                sx={{
                  height: '40px',
                  mb: 6,
                }}
              />

              {/* Login Card - Glassmorphic */}
              <Card
                sx={{
                  width: '100%',
                  maxWidth: 360,
                  borderRadius: '20px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
                  backgroundColor: 'rgba(255, 255, 255, 0.75)',
                  backdropFilter: 'blur(24px)',
                  WebkitBackdropFilter: 'blur(24px)',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  overflow: 'visible',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    component="h1"
                    align="center"
                    sx={{
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      mb: 3,
                      color: '#1D1D1F',
                      letterSpacing: '-0.02em',
                      fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
                    }}
                  >
                    Welcome back
                  </Typography>

                  {infoMessage && (
                    <Alert
                      severity="info"
                      sx={{ mb: 3, borderRadius: '12px' }}
                      onClose={() => setInfoMessage('')}
                    >
                      {infoMessage}
                    </Alert>
                  )}

                  {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                      {error}
                    </Alert>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleMicrosoftLogin}
                    disabled={ssoProcessing}
                    sx={{
                      height: '42px',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '10px',
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                      background: 'linear-gradient(135deg, #FB923C 0%, #F97316 100%)',
                      boxShadow: '0 2px 8px -2px rgba(249, 115, 22, 0.3)',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                        boxShadow: '0 4px 12px -2px rgba(249, 115, 22, 0.4)',
                      },
                      '&:active': {
                        transform: 'scale(0.98)',
                      },
                      '&.Mui-disabled': {
                        background: 'linear-gradient(135deg, #FDBA74 0%, #FB923C 100%)',
                        color: 'rgba(255, 255, 255, 0.8)',
                      },
                    }}
                  >
                    {ssoProcessing ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Sign in with Microsoft'
                    )}
                  </Button>
                </CardContent>
              </Card>
            </Box>
          </Container>
        </Box>
      </PageTransition>
    </ThemeProvider>
  );
};

export default Login;
